import {
  minutesToTimeString,
  scheduleWindowsOverlap,
} from '../utils/scheduleConflict';
import { jobLegOccupancyEndMinutes } from './occupancyWindow';
import type { DailySchedulePlan } from './types';

export interface PlanAssignmentWindow {
  vehicleId: string;
  jobId: string;
  pickupTime: string;
  endTime: string;
}

export interface PlanDriverAssignmentWindow {
  vehicleId: string;
  driverId: string;
  jobId: string;
  legId: string;
  pickupTime: string;
  endTime: string;
}

export interface IntraPlanDriverConflict {
  driverId: string;
  a: PlanDriverAssignmentWindow;
  b: PlanDriverAssignmentWindow;
}

/**
 * Time windows for newly planned (non-fixed) jobs only — used before publish
 * to call check_vehicle_schedule_conflict without self-matching published blocks.
 */
export function planNewAssignmentWindows(plan: DailySchedulePlan): PlanAssignmentWindow[] {
  const windows: PlanAssignmentWindow[] = [];

  for (const vehicle of plan.vehicles) {
    const newJobIds = [
      ...new Set(vehicle.legs.filter((l) => !l.isFixed).map((l) => l.jobId)),
    ];
    for (const jobId of newJobIds) {
      const entries = vehicle.validation.timeline.filter((t) => t.jobId === jobId);
      if (entries.length === 0) {
        const legs = vehicle.legs.filter((l) => l.jobId === jobId && !l.isFixed);
        if (legs.length === 0) continue;
        const start = Math.min(...legs.map((l) => l.earliestStartMinutes));
        const end = Math.max(...legs.map((l) => jobLegOccupancyEndMinutes(l)));
        windows.push({
          vehicleId: vehicle.vehicleId,
          jobId,
          pickupTime: minutesToTimeString(start),
          endTime: minutesToTimeString(end),
        });
        continue;
      }
      const start = Math.min(...entries.map((e) => e.arriveMinutes));
      const end = Math.max(...entries.map((e) => e.departMinutes));
      windows.push({
        vehicleId: vehicle.vehicleId,
        jobId,
        pickupTime: minutesToTimeString(start),
        endTime: minutesToTimeString(end),
      });
    }
  }

  return windows;
}

/**
 * Per-leg windows for drivers on newly planned legs — used to detect
 * same-driver overlaps across vehicles before publish_schedule_plan.
 */
export function planNewDriverAssignmentWindows(
  plan: DailySchedulePlan
): PlanDriverAssignmentWindow[] {
  const windows: PlanDriverAssignmentWindow[] = [];

  for (const vehicle of plan.vehicles) {
    const driverId = vehicle.driverId;
    if (!driverId) continue;

    for (const leg of vehicle.legs) {
      if (leg.isFixed) continue;
      const entry = vehicle.validation.timeline.find((t) => t.legId === leg.legId);
      const start = entry?.arriveMinutes ?? leg.earliestStartMinutes;
      const end = entry?.departMinutes ?? jobLegOccupancyEndMinutes(leg);
      windows.push({
        vehicleId: vehicle.vehicleId,
        driverId,
        jobId: leg.jobId,
        legId: leg.legId,
        pickupTime: minutesToTimeString(start),
        endTime: minutesToTimeString(end),
      });
    }
  }

  return windows;
}

/** Same driver on two new legs whose windows overlap (incl. buffer). */
export function findIntraPlanDriverConflicts(
  plan: DailySchedulePlan,
  bufferMinutes: number
): IntraPlanDriverConflict[] {
  const windows = planNewDriverAssignmentWindows(plan);
  const conflicts: IntraPlanDriverConflict[] = [];

  for (let i = 0; i < windows.length; i++) {
    for (let j = i + 1; j < windows.length; j++) {
      const a = windows[i];
      const b = windows[j];
      if (a.driverId !== b.driverId) continue;
      if (a.legId === b.legId) continue;
      if (
        scheduleWindowsOverlap(
          { pickupTime: a.pickupTime, endTime: a.endTime },
          { pickupTime: b.pickupTime, endTime: b.endTime },
          bufferMinutes
        )
      ) {
        conflicts.push({ driverId: a.driverId, a, b });
      }
    }
  }

  return conflicts;
}
